import PrepaidCardApi from '@apis/prepaid/PrepaidCardApi';
import PrepaidContext from '@components/prepaid/PrepaidProvider/PrepaidContext';
import usePrepaidCardPayment from '@hooks/prepaidCard/usePrepaidCardPayment';
import useProjectId from '@hooks/useProjectId';
import { ErrorResponse } from '@models/error';
import { useMutation } from '@tanstack/react-query';
import { setIsPrepaidCardBalanceUpdated } from '@utils/prepaidCardUtils';
import { AxiosError } from 'axios';
import { useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

function usePrepaidCharge(id: string) {
    const { t } = useTranslation('prepaidCard');
    const { amount } = useContext(PrepaidContext);
    const projectId = useProjectId();
    const openPayment = usePrepaidCardPayment();
    const submitting = useRef(false);

    const { mutate, isPending } = useMutation({
        mutationFn: () => PrepaidCardApi.chargePrepaidCard(id, {
            projectId,
            amount: amount * 10000,
            payMethodType: 'CREDIT_CARD',
        }),
        onSuccess(res) {
            setIsPrepaidCardBalanceUpdated(true);
            openPayment(res, amount * 10000, `${t('charge.itemName')} ${(amount * 10000).toLocaleString()}${t('currencyUnit')}`);
        },
        onSettled() {
            submitting.current = false;
        },
        onError(e: AxiosError<ErrorResponse>) {
            toast.error(e.response?.data?.message || t('charge.error'));
        },
    });

    const handleCharge = () => {
        if (submitting.current) return;
        if (!Number.isSafeInteger(amount * 10000) || amount <= 0) {
            toast.error(t('charge.error'));
            return;
        }
        submitting.current = true;
        mutate();
    };

    return { handleCharge, isPending };
}

export default usePrepaidCharge;
